import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { get, put } from 'shared-components/src/utils/http';
import PropTypes from 'prop-types';
import {
  ActionBar,
  ButtonGroup,
  Icon,
  Input,
  Loader,
  Modal,
} from 'shared-components';
import { isExemptBin } from 'shared-components/src/utils/binExemption';
import { digitsOnly } from '../../../../utils/cardFormatting';
import {
  CONTACT_COUNTRY,
  contactCardErrors,
  formatPostalCode,
  toProvinceKey,
} from '../../../../utils/contactCard';
import {
  CardNumberInput,
  PostalCodeInput,
  ProvinceSelect,
  CountrySelect,
  ContactFieldError,
} from '../ContactFields';
import useFieldErrors from '../ContactFields/useFieldErrors';
import { InputItem, InputRow, ModalFormContainer, StyledLabel, CollapsibleSection, InfoRow } from './styles';

import SubmitButton from '../../../../components/SubmitButton';
import CancelButton from '../../../../components/CancelButton';
import Alert from '../../../../components/Alert';

const EditExternalDirectsendAccountV2Modal = ({
  toggleModal,
  setSuccess,
  setExternalAccounts,
  page,
  id,
}) => {
  const intl = useIntl();
  const [formData, setFormData] = useState({
    name: '',
    fullName: '',
    cardNumber: '',
    expiry: '',
    cvv: '',
    addressLineOne: '',
    addressLineTwo: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
  });

  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastFourDigits, setlastFourDigits] = useState('****');

  const isBinExempt = isExemptBin(formData.cardNumber);
  const cardNumberLength = digitsOnly(formData.cardNumber).length;
  const direction = page === 'directsend-push' ? 'push' : 'pull';
  const { fieldErrors, fieldChanged, validateField, validateAll, clearField, invalid } = useFieldErrors(() =>
    contactCardErrors(formData)
  );

  const fetchExternalAccount = async () => {
    try {
      return await get(
        `/api/v2/cardholder_portal/cardholder/external_accounts/${id}/details`
      );
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch external account');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const externalAccount = await fetchExternalAccount(id);
        if (externalAccount) {
          setlastFourDigits(externalAccount.card.last_four_digits);
          setFormData({
            name: externalAccount.name,
            fullName: externalAccount.card.name,
            cardNumber: '',
            expiry: externalAccount.card.expiry_month && externalAccount.card.expiry_year ? `${externalAccount.card.expiry_month}/${externalAccount.card.expiry_year}` : '',
            cvv: '',
            addressLineOne: externalAccount.card.address_line1,
            addressLineTwo: externalAccount.card.address_line2,
            city: externalAccount.card.address_city,
            // Stored values open in the form the fields use ("on" -> "ON", "k1a0b1" -> "K1A 0B1").
            state: toProvinceKey(externalAccount.card.address_state),
            country: CONTACT_COUNTRY,
            postalCode: formatPostalCode(externalAccount.card.address_postal_code),
          });
        }
      } catch (error) {
        setAlert({ type: 'danger', message: error.message });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const updateContact = async () => {
    const {
      name,
      fullName,
      cardNumber,
      expiry,
      cvv,
      addressLineOne,
      addressLineTwo,
      city,
      state,
      country,
      postalCode,
    } = formData;

    try {
      const cardPayload = {
        name: fullName,
        number: digitsOnly(cardNumber),
        address_line1: addressLineOne,
        address_line2: addressLineTwo,
        address_city: city,
        address_state: state,
        address_country: country,
        address_postal_code: formatPostalCode(postalCode),
      };

      if (expiry && !isBinExempt) {
        const [expiryMonth, expiryYear] = expiry.split('/');
        cardPayload.expiry_month = expiryMonth;
        cardPayload.expiry_year = expiryYear;
      }

      if (cvv && !isBinExempt) {
        cardPayload.cvv = cvv;
      }

      const externalAccountPayload = {
        name,
        card: cardPayload,
      };

      await put(
        `/api/v2/cardholder_portal/cardholder/external_accounts/${id}`,
        externalAccountPayload
      );

      const response = await get(
        `/api/v1/cardholder_portal/cardholder/external_accounts/${id}`
      );

      return response.data;
    } catch (error) {
      throw new Error(error.message || 'Failed to update external account');
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateAll()) return;

    setLoading(true);
    setAlert(null);

    try {
      const updatedContact = await updateContact();
      setExternalAccounts(externalAccounts =>
        externalAccounts.map(account =>
          account.id === updatedContact.id ? updatedContact : account
        )
      );

      setSuccess({
        type: 'success',
        message: intl.messages[`${page}-success-update-alert`],
        messageFor: 'success-update',
        component: page,
        page,
      });

      setLoading(false);
      toggleModal(false);
    } catch (error) {
      setAlert({ type: 'danger', message: error.message });
      setLoading(false);
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  return (
    <Modal>
      <ModalFormContainer>
        {alert && (
          <Alert
            alert={alert.message}
            alertFor={alert.messageFor}
            page={page}
            type={alert.type}
          />
        )}
        <h2>{intl.messages[`${page}-update-contact-heading`]}</h2>
        {loading ? (
          <Loader />
        ) : (
          // noValidate: every field is checked as it is left and on submit, with its message
          // shown under it, instead of the browser's own bubble.
          <form noValidate onChange={fieldChanged} onSubmit={e => handleSubmit(e)}>
            <InputRow>
              <InputItem>
                <StyledLabel htmlFor="contact-name-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-contact-name-input-label`]}
                </StyledLabel>
                <Input
                  id="contact-name-input"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={invalid('name')}
                  onBlur={() => validateField('name')}
                  required
                />
                <ContactFieldError error={fieldErrors.name} />
              </InputItem>
              <InputItem>
                <StyledLabel htmlFor="contact-full-cardholder-name-input">
                  <span>*</span>{' '}
                  {
                    intl.messages[
                      `${page}-contact-full-cardholder-name-input-label`
                    ]
                  }
                </StyledLabel>
                <Input
                  id="contact-full-cardholder-name-input"
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  className={invalid('fullName')}
                  onBlur={() => validateField('fullName')}
                  required
                />
                <ContactFieldError error={fieldErrors.fullName} />
              </InputItem>
            </InputRow>
            <StyledLabel htmlFor="contact-card-number-input">
              <span>*</span>{' '}
              {intl.messages[`contact-fields-card-number-label-${direction}`]}
            </StyledLabel>
            <CardNumberInput
              id="contact-card-number-input"
              direction={direction}
              name="cardNumber"
              value={formData.cardNumber}
              onChange={handleChange}
              placeholder={`**** **** **** ${lastFourDigits}`}
              className={invalid('cardNumber')}
              onBlur={() => validateField('cardNumber')}
              required
            />
            <ContactFieldError error={fieldErrors.cardNumber} />
            {/* CVV + Expiry - hidden by default, show only when card number >= 6 digits and BIN is not exempt */}
            <CollapsibleSection $visible={cardNumberLength >= 6 && !isBinExempt}>
              <InputRow>
                <InputItem>
                  <StyledLabel htmlFor="contact-expiry-input">
                    <span>*</span>{' '}
                    {intl.messages[`${page}-contact-expiry-input-label`]}
                  </StyledLabel>
                  <Input
                    id="contact-expiry-input"
                    type="text"
                    name="expiry"
                    value={formData.expiry}
                    onChange={handleChange}
                    placeholder={intl.messages['contact-fields-expiry-placeholder']}
                    className={invalid('expiry')}
                    onBlur={() => validateField('expiry')}
                    required={cardNumberLength >= 6 && !isBinExempt}
                  />
                  <ContactFieldError error={fieldErrors.expiry} />
                </InputItem>
                <InputItem>
                  <StyledLabel htmlFor="contact-cvv-input">
                    <span>*</span>{' '}
                    {intl.messages[`${page}-contact-cvv-input-label`]}
                  </StyledLabel>
                  <Input
                    id="contact-cvv-input"
                    type="text"
                    name="cvv"
                    value={formData.cvv}
                    onChange={handleChange}
                    placeholder="***"
                    className={invalid('cvv')}
                    onBlur={() => validateField('cvv')}
                    required={cardNumberLength >= 6 && !isBinExempt}
                  />
                  <ContactFieldError error={fieldErrors.cvv} />
                </InputItem>
              </InputRow>
            </CollapsibleSection>
            {/* Info message when CVV/Expiry not needed */}
            <InfoRow $visible={cardNumberLength > 0 && cardNumberLength >= 6 && isBinExempt}>
              <Icon type="info" size={16} />
              <span>{intl.messages['visa.noCvvNeeded'] || 'No CVV needed.'}</span>
            </InfoRow>
            <StyledLabel htmlFor="contact-address-line-one-input">
              <span>*</span>{' '}
              {intl.messages[`${page}-contact-address-line-one-input-label`]}
            </StyledLabel>
            <Input
              id="contact-address-line-one-input"
              type="text"
              name="addressLineOne"
              value={formData.addressLineOne}
              onChange={handleChange}
              className={invalid('addressLineOne')}
              onBlur={() => validateField('addressLineOne')}
              required
            />
            <ContactFieldError error={fieldErrors.addressLineOne} />
            <StyledLabel htmlFor="contact-address-line-two-input">
              {intl.messages[`${page}-contact-address-line-two-input-label`]}
            </StyledLabel>
            <Input
              id="contact-address-line-two-input"
              type="text"
              name="addressLineTwo"
              value={formData.addressLineTwo}
              onChange={handleChange}
            />
            <InputRow>
              <InputItem>
                <StyledLabel htmlFor="contact-city-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-contact-city-input-label`]}
                </StyledLabel>
                <Input
                  id="contact-city-input"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className={invalid('city')}
                  onBlur={() => validateField('city')}
                  required
                />
                <ContactFieldError error={fieldErrors.city} />
              </InputItem>
              <InputItem>
                <StyledLabel htmlFor="contact-state-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-contact-state-input-label`]}
                </StyledLabel>
                <ProvinceSelect
                  id="contact-state-input"
                  value={formData.state}
                  onChange={state => {
                    setFormData(prevState => ({ ...prevState, state }));
                    clearField('state');
                  }}
                  invalid={Boolean(fieldErrors.state)}
                />
                <ContactFieldError error={fieldErrors.state} />
              </InputItem>
            </InputRow>
            <InputRow>
              <InputItem>
                <StyledLabel htmlFor="contact-postal-code-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-contact-postal-code-input-label`]}
                </StyledLabel>
                <PostalCodeInput
                  id="contact-postal-code-input"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  className={invalid('postalCode')}
                  onBlur={() => validateField('postalCode')}
                  required
                />
                <ContactFieldError error={fieldErrors.postalCode} />
              </InputItem>
              <InputItem>
                <StyledLabel htmlFor="contact-country-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-contact-country-input-label`]}
                </StyledLabel>
                <CountrySelect id="contact-country-input" />
              </InputItem>
            </InputRow>
            <ActionBar>
              <ButtonGroup reverse>
                <SubmitButton />
                <CancelButton
                  onClick={e => {
                    e.preventDefault();
                    toggleModal(false);
                  }}
                />
              </ButtonGroup>
            </ActionBar>
          </form>
        )}
      </ModalFormContainer>
    </Modal>
  );
};

EditExternalDirectsendAccountV2Modal.propTypes = {
  toggleModal: PropTypes.func.isRequired,
  setSuccess: PropTypes.func.isRequired,
  setExternalAccounts: PropTypes.func.isRequired,
  page: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
};

export default EditExternalDirectsendAccountV2Modal;
